/**
 * Food Distribution Service
 * Public check-in endpoints use raw fetch (no JWT).
 * Staff endpoints use the authenticated apiGet/apiPost/apiPut helpers.
 */
import { apiGet, apiPost, apiPut } from './api';
import {
  FDClient,
  FDClientLookupResult,
  FDIntakePreFillData,
  FDVisit,
  FDReferral,
  FDClientProgressRecord,
  FDCreateClientRequest,
  FDCreateVisitRequest,
  FDRegisterAndVisitRequest,
  FDRegisterAndVisitResponse,
  FDClientFilters,
  FDVisitFilters,
  FDReferralFilters,
  FDReportParams,
} from '../types/foodDistribution';
import { PaginatedResponse } from '../types/api';

// Public base URL — same pattern as KioskCodeEntryPage
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';

// ─── PUBLIC CHECK-IN ENDPOINTS (no JWT) ───────────────────────────────────────

/**
 * Look up an existing FD client by phone number.
 * Returns a list of matches (first name + last initial) for privacy.
 */
export async function lookupByPhone(phone: string): Promise<FDClientLookupResult[]> {
  const response = await fetch(`${API_BASE_URL}/food/check-in/lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    const serverMsg = result.error?.message || '';
    throw new Error(serverMsg || 'Unable to look up client. Please try again.');
  }

  return result.data as FDClientLookupResult[];
}

/**
 * Register a new FD client at the kiosk (public, no JWT).
 */
export async function createFDClientPublic(data: FDCreateClientRequest): Promise<FDClient> {
  const response = await fetch(`${API_BASE_URL}/food/check-in/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    const serverMsg = result.error?.message || '';
    throw new Error(serverMsg || 'Unable to register client. Please try again.');
  }

  return result.data as FDClient;
}

/**
 * Record a food distribution visit at the kiosk (public, no JWT).
 */
export async function createFDVisitPublic(data: FDCreateVisitRequest): Promise<FDVisit> {
  const response = await fetch(`${API_BASE_URL}/food/check-in/visit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    const serverMsg = result.error?.message || '';
    throw new Error(serverMsg || 'Unable to record visit. Please try again.');
  }

  const payload = result.data;
  return (payload.visit ?? payload) as FDVisit;
}

/**
 * Atomically register a new FD client AND record their first visit (public kiosk).
 * Defers client creation until the full check-in is complete, preventing orphan records.
 */
export async function registerAndVisitPublic(
  data: FDRegisterAndVisitRequest
): Promise<FDRegisterAndVisitResponse> {
  const response = await fetch(`${API_BASE_URL}/food/check-in/register-and-visit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    const serverMsg = result.error?.message || '';
    throw new Error(serverMsg || 'Unable to complete registration. Please try again.');
  }

  return result.data as FDRegisterAndVisitResponse;
}

// ─── STAFF ENDPOINTS (authenticated) ──────────────────────────────────────────

/**
 * List FD clients with optional filtering and pagination.
 */
export async function getFDClients(params?: FDClientFilters): Promise<PaginatedResponse<FDClient>> {
  const response = await apiGet<FDClient[]>('/fd/clients', params);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch FD clients error:', { params, error: response.error });
    throw new Error(serverMsg || 'Unable to load food distribution clients. Please try again.');
  }

  return response as PaginatedResponse<FDClient>;
}

/**
 * Get a single FD client by ID.
 */
export async function getFDClient(id: number): Promise<FDClient> {
  const response = await apiGet<FDClient>(`/fd/clients/${id}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load food distribution client. Please try again.');
  }

  return response.data;
}

/**
 * Update an FD client record (staff use).
 */
export async function updateFDClient(id: number, data: Partial<FDClient>): Promise<FDClient> {
  const response = await apiPut<FDClient>(`/fd/clients/${id}`, data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update food distribution client. Please try again.');
  }

  return response.data;
}

/**
 * Link an FD client to an existing CRM individual record.
 */
export async function linkFDClient(id: number, individualId: number): Promise<FDClient> {
  const response = await apiPost<FDClient>(`/fd/clients/${id}/link`, { individualId });

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to link food distribution client. Please try again.');
  }

  return response.data;
}

/**
 * Get FD client data formatted for CRM intake form pre-fill.
 */
export async function getFDClientForIntake(fdClientId: number): Promise<FDIntakePreFillData> {
  const response = await apiGet<FDIntakePreFillData>(`/fd/clients/${fdClientId}/intake-data`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load intake data. Please try again.');
  }

  return response.data;
}

/**
 * List visits for a specific FD client.
 */
export async function getFDVisits(
  clientId: number,
  params?: FDVisitFilters
): Promise<PaginatedResponse<FDVisit>> {
  const response = await apiGet<FDVisit[]>(`/fd/clients/${clientId}/visits`, params);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch FD visits error:', { clientId, params, error: response.error });
    throw new Error(serverMsg || 'Unable to load food distribution visits. Please try again.');
  }

  return response as PaginatedResponse<FDVisit>;
}

/**
 * Get a single FD visit by visit ID.
 */
export async function getFDVisit(id: number): Promise<FDVisit> {
  const response = await apiGet<FDVisit>(`/fd/visits/${id}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load food distribution visit. Please try again.');
  }

  return response.data;
}

/**
 * Update staff_user and/or notes on a visit (staff use).
 */
export async function updateFDVisit(
  id: number,
  data: { staffUser?: string; notes?: string }
): Promise<FDVisit> {
  const response = await apiPut<FDVisit>(`/fd/visits/${id}`, data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update food distribution visit. Please try again.');
  }

  return response.data;
}

/**
 * List referrals with optional filtering and pagination.
 */
export async function getFDReferrals(
  params?: FDReferralFilters
): Promise<PaginatedResponse<FDReferral>> {
  const response = await apiGet<FDReferral[]>('/fd/referrals', params);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch FD referrals error:', { params, error: response.error });
    throw new Error(serverMsg || 'Unable to load food distribution referrals. Please try again.');
  }

  return response as PaginatedResponse<FDReferral>;
}

/**
 * Update an FD referral's engagement status or notes.
 */
export async function updateFDReferral(id: number, data: Partial<FDReferral>): Promise<FDReferral> {
  const response = await apiPut<FDReferral>(`/fd/referrals/${id}`, data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update food distribution referral. Please try again.');
  }

  return response.data;
}

/**
 * Get food distribution summary report.
 */
export async function getFDReportsSummary(params?: FDReportParams): Promise<unknown> {
  const response = await apiGet<unknown>('/fd/reports/summary', params);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load summary report. Please try again.');
  }

  return response.data;
}

/**
 * Get food security outcomes report (USDA 6-item scores).
 */
export async function getFDReportsOutcomes(params?: FDReportParams): Promise<unknown> {
  const response = await apiGet<unknown>('/fd/reports/outcomes', params);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load outcomes report. Please try again.');
  }

  return response.data;
}

/**
 * Get client progress report (first vs latest visit comparison).
 */
export async function getFDReportsProgress(
  params?: FDReportParams
): Promise<FDClientProgressRecord[]> {
  const response = await apiGet<FDClientProgressRecord[]>('/fd/reports/progress', params);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load progress report. Please try again.');
  }

  return response.data;
}

/**
 * Get service needs aggregation report.
 */
export async function getFDReportsServiceNeeds(params?: FDReportParams): Promise<unknown> {
  const response = await apiGet<unknown>('/fd/reports/service-needs', params);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load service needs report. Please try again.');
  }

  return response.data;
}

/**
 * Export FD data to CSV/Excel.
 */
export async function getFDReportsExport(params?: FDReportParams): Promise<unknown> {
  const response = await apiGet<unknown>('/fd/reports/export', params);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to export report. Please try again.');
  }

  return response.data;
}
